import { GUEST_DAILY_SEARCH_LIMIT } from './search-limits'

export type AppPlan = 'free' | 'pro'
export type EntitlementSource = 'none' | 'subscription' | 'admin_grant'
export type EntitlementStatus = 'inactive' | 'active'
export type BillingStatus =
  | 'none'
  | 'pending'
  | 'active'
  | 'on_hold'
  | 'cancelled'
  | 'failed'
  | 'expired'
  | 'unknown'
export type BillingPlan = 'weekly' | 'yearly'
export type PaywallType = 'premium-chapters'

export interface PlanLimits {
  maxDocumentSizeKb: number
  premiumStoriesAccess: boolean
}

export interface AccountEntitlementRecord {
  plan?: string | null
  source?: string | null
  status?: string | null
  expires_at?: string | null
}

export interface ResolvedEntitlement {
  plan: AppPlan
  isPremium: boolean
  source: EntitlementSource
  status: EntitlementStatus
  limits: PlanLimits
}

export const BILLING_PLANS = ['weekly', 'yearly'] as const

export const PAYWALL_TYPES = ['premium-chapters'] as const

export const DODO_SUBSCRIPTION_EVENTS = [
  'subscription.active',
  'subscription.updated',
  'subscription.renewed',
  'subscription.plan_changed',
  'subscription.on_hold',
  'subscription.cancelled',
  'subscription.failed',
  'subscription.expired'
] as const

export const PLAN_LIMITS: Record<AppPlan, PlanLimits> = {
  free: {
    maxDocumentSizeKb: 5000,
    premiumStoriesAccess: false
  },
  pro: {
    maxDocumentSizeKb: 5000,
    premiumStoriesAccess: true
  }
}

export const PRO_FEATURES: Array<{
  title: string
  description: string
  paywallTypes: PaywallType[]
}> = [
  {
    title: 'Pro stories and chapters',
    description:
      'Read selected graded stories and chapters in the full reader.',
    paywallTypes: ['premium-chapters']
  }
]

export const GUEST_LIMITS = {
  dailySearches: GUEST_DAILY_SEARCH_LIMIT,
  canCreateCollections: false,
  canUploadDocuments: false,
  canStudyFlashcards: false
} as const

export function getPlanLimits(plan: AppPlan): PlanLimits {
  return PLAN_LIMITS[plan]
}

export function isBillingPlan(value: unknown): value is BillingPlan {
  return BILLING_PLANS.includes(value as BillingPlan)
}

export function isPaywallType(value: unknown): value is PaywallType {
  return PAYWALL_TYPES.includes(value as PaywallType)
}

export function normalizeBillingStatus(value: unknown): BillingStatus {
  if (typeof value !== 'string' || !value.trim()) return 'none'
  const normalized = value.trim().toLowerCase()

  if (
    normalized === 'pending' ||
    normalized === 'active' ||
    normalized === 'on_hold' ||
    normalized === 'cancelled' ||
    normalized === 'failed' ||
    normalized === 'expired'
  ) {
    return normalized
  }

  return 'unknown'
}

export function isActiveSubscriptionStatus(value: unknown): boolean {
  return normalizeBillingStatus(value) === 'active'
}

export function normalizeEntitlementSource(value: unknown): EntitlementSource {
  if (value === 'subscription' || value === 'admin_grant') return value
  return 'none'
}

export function resolveAccountEntitlement({
  record,
  paywallsEnabled = true,
  now = new Date()
}: {
  record?: AccountEntitlementRecord | null
  paywallsEnabled?: boolean
  now?: Date
}): ResolvedEntitlement {
  if (!paywallsEnabled) {
    return {
      plan: 'pro',
      isPremium: true,
      source: 'none',
      status: 'active',
      limits: PLAN_LIMITS.pro
    }
  }

  const source = normalizeEntitlementSource(record?.source)
  const isActive = record?.status === 'active'
  const isPro = record?.plan === 'pro'
  const expiresAt = record?.expires_at ? new Date(record.expires_at) : null
  const isExpired =
    expiresAt !== null &&
    Number.isFinite(expiresAt.getTime()) &&
    expiresAt.getTime() <= now.getTime()

  if (isPro && isActive && !isExpired && source !== 'none') {
    return {
      plan: 'pro',
      isPremium: true,
      source,
      status: 'active',
      limits: PLAN_LIMITS.pro
    }
  }

  return {
    plan: 'free',
    isPremium: false,
    source: 'none',
    status: 'inactive',
    limits: PLAN_LIMITS.free
  }
}

export function getPublicPricing(): { weeklyUsd: number; yearlyUsd: number } {
  return {
    weeklyUsd: getNumericEnv('NEXT_PUBLIC_PRO_WEEKLY_PRICE', 0.99),
    yearlyUsd: getNumericEnv('NEXT_PUBLIC_PRO_YEARLY_PRICE', 19.99)
  }
}

function getNumericEnv(name: string, fallback: number): number {
  const raw = process.env[name]
  if (!raw) return fallback
  const value = Number(raw)
  return Number.isFinite(value) && value > 0 ? value : fallback
}
